import { GameUIMessageType, ViewportType } from 'game-ui-types'
import { trackEvent } from '@utils/analytics'
import {
    useBuyBonusStore,
    useConfigurationStore,
    useDoubleChanceStore,
    useSpinButtonSectionStore,
    useBetSliderStore,
} from '@stores'

// The game's GA4 event vocabulary. Every event name and parameter shape lives here so call sites stay
// one-liners and the reporting side has a single place to read the contract from. Ultra Play keeps its
// own, much richer session vocabulary in @components/UltraPlay/ultraPlayAnalytics.
//
// The engine (base-slot) has no GA transport of its own once its legacy events were removed: it posts
// ENGINE_EVENT_MESSAGE_TYPE onto the window and installGameAnalytics() forwards it through
// trackEvent, so engine-side events get the same common parameters as UI-side ones.

/** Window message base-slot uses to hand an event to this module. Kept as a literal on both sides
 *  because game-ui-types is published separately and this is a private channel between the two. */
export const ENGINE_EVENT_MESSAGE_TYPE = 'gaEvent'

export type BetTrigger = 'bet_button' | 'spin_button' | 'space_bar' | 'autoplay' | 'ultra_play'
export type BetChangeSource = 'bet_slider' | 'bet_stepper' | 'bet_options'
export type ReelSpeedName = 'normal' | 'fast' | 'stop_all' | 'ultra'
export type JackpotWinType = 'last' | 'top'

// ReelSpeed (@amusnet/component-reels) by index — the engine syncs it into speedButtonLevel.
const SPEED_NAMES: ReelSpeedName[] = ['normal', 'fast', 'stop_all', 'ultra']

const deviceType = (): 'mobile' | 'desktop' | 'tablet' => {
    const device = (window.egtLibrary || window.amusnetLibrary)?.Device

    if (device?.isTablet) return 'tablet'

    return device?.isMobile ? 'mobile' : 'desktop'
}

const deviceOrientation = (): 'portrait' | 'landscape' =>
    useConfigurationStore.getState().viewport === ViewportType.MOBILE_PORTRAIT ? 'portrait' : 'landscape'

const deviceParams = () => ({
    device_type: deviceType(),
    device_orientation: deviceOrientation(),
})

const speedName = (): ReelSpeedName => SPEED_NAMES[useSpinButtonSectionStore.getState().speedButtonLevel] ?? 'normal'

// A buy bonus confirmed in the modal for a game that does not keep the feature active applies to the
// next bet only, and leaves no store state to read it back from — so it is remembered here.
let pendingModalBuyBonus: string | null = null

const isBuyBonusBet = () =>
    pendingModalBuyBonus !== null ||
    useBuyBonusStore.getState().isMultiChoiceToggled ||
    useBuyBonusStore.getState().currentFeatureMultiplier > 1

/** Which feature, if any, the current bet is being played with. */
const activeFeature = (): 'off' | 'buy_bonus' | 'double_chance' => {
    if (useDoubleChanceStore.getState().isDoubleChanceActivated) return 'double_chance'

    return isBuyBonusBet() ? 'buy_bonus' : 'off'
}

const currentBetOption = () => {
    const { betOptions } = useConfigurationStore.getState()
    const { activeIndex, getActiveBetOption } = useBetSliderStore.getState()
    const option = getActiveBetOption() ?? betOptions[activeIndex]

    return typeof option === 'number' ? option : (option?.betAmount ?? 0)
}

// What started the bet that the engine is about to request. Set by whichever control initiated it and
// read back when BET_REQUESTED arrives, since the engine message itself carries no origin.
let pendingBetTrigger: BetTrigger = 'spin_button'
// Turbo (long-press) spins run at STOP_ALL regardless of the selected speed level.
let isPendingBetTurbo = false

let isInstalled = false

const betMade = () => {
    const { isSoundMuted, isBackgroundMusicMuted } = useConfigurationStore.getState()
    const feature = activeFeature()

    trackEvent('bet_made', {
        bet_option: currentBetOption(),
        speed: isPendingBetTurbo ? 'stop_all' : speedName(),
        triggered_from: pendingBetTrigger,
        feature,
        sound_status: isSoundMuted ? 'disabled' : 'enabled',
        background_music_status: isBackgroundMusicMuted ? 'disabled' : 'enabled',
        ...deviceParams(),
    })

    // Desktop mouse-vs-spacebar split, kept as separate events so it can be read without registering
    // custom dimensions.
    if (deviceType() === 'desktop') {
        trackEvent(pendingBetTrigger === 'space_bar' ? 'bet_made_with_spacebar' : 'bet_made_with_mouse_click')
    }

    if (feature === 'buy_bonus') {
        const { isMultiChoiceToggled, selectedFeature } = useBuyBonusStore.getState()

        trackEvent('buy_bonus_purchased', {
            triggered_from: pendingModalBuyBonus === null && isMultiChoiceToggled ? 'persistent_bb' : 'bb_modal',
            feature_name: pendingModalBuyBonus ?? selectedFeature?.name,
            bet_option: currentBetOption(),
        })

        pendingModalBuyBonus = null
    }
}

/**
 * One-time window listeners: the engine bridge and the per-bet BET_MADE event. Called from App once
 * analytics has been initialised.
 */
export const installGameAnalytics = () => {
    if (isInstalled) return

    isInstalled = true

    window.addEventListener('message', (event: MessageEvent) => {
        const { type, payload } = event.data ?? {}

        if (type === ENGINE_EVENT_MESSAGE_TYPE && payload?.name) {
            trackEvent(payload.name, payload.params ?? {})

            return
        }

        // Every bet the engine actually places passes through here — spin button, space, autoplay and
        // Ultra Play alike — which makes it the one place BET_MADE can be complete. Free spins are
        // re-spins of a bet already reported, so they are skipped.
        if (type === GameUIMessageType.BET_REQUESTED && !event.data.payload?.isFreespin) {
            betMade()
        }
    })
}

export const gameAnalytics = {
    gameOpened: () => trackEvent('game_opened', deviceParams()),

    betChanged: (triggeredFrom: BetChangeSource) => trackEvent('bet_changed', { triggered_from: triggeredFrom }),

    /** Recorded by the control that starts a bet; read back when the engine requests it. */
    setBetTrigger: (trigger: BetTrigger, isTurbo = false) => {
        pendingBetTrigger = trigger
        isPendingBetTurbo = isTurbo
    },

    autoplayStarted: (numberOfSpins: number, triggeredFrom: 'settings' | 'autoplay_button') =>
        trackEvent('autoplay_started', { number_of_spins: numberOfSpins, triggered_from: triggeredFrom }),

    soundToggled: (isMuted: boolean) => trackEvent(isMuted ? 'sound_disabled' : 'sound_enabled'),
    backgroundMusicToggled: (isMuted: boolean) =>
        trackEvent(isMuted ? 'background_music_disabled' : 'background_music_enabled'),

    fullScreenToggled: (isFullscreen: boolean) =>
        trackEvent(isFullscreen ? 'full_screen_entered' : 'full_screen_exited'),

    homeButtonClicked: () => trackEvent('home_button_clicked'),
    infoPageOpened: () => trackEvent('info_page_opened'),
    gameSettingsOpened: () => trackEvent('game_settings_opened'),
    historyOpened: () => trackEvent('history_opened'),

    jackpotStatisticsOpened: (selectedTab: string, selectedWinType: JackpotWinType) =>
        trackEvent('jackpot_statistics_opened', {
            selected_tab: selectedTab.toLowerCase(),
            selected_win_type: selectedWinType,
        }),
    /**
     * Not wired yet: @amusnet/react-ui's jackpot history owns its filter and Last/Top sort state and
     * exposes no callback for either, so a change inside the open panel is not observable from here.
     * Kept so the call site is a one-liner once the component reports them.
     */
    jackpotStatisticViewChanged: (selectedTab: string, selectedWinType: JackpotWinType) =>
        trackEvent('jackpot_statistic_view_changed', {
            selected_tab: selectedTab.toLowerCase(),
            selected_win_type: selectedWinType,
        }),

    buyBonusModalOpened: () => trackEvent('buy_bonus_modal_opened'),
    /** The confirmed feature applies to the next bet, which is where the purchase is reported. */
    buyBonusConfirmed: (featureName: string) => {
        pendingModalBuyBonus = featureName
    },
    /** Closed without buying — the confirm path reports buy_bonus_purchased on the bet instead. */
    buyBonusModalClosed: () => trackEvent('buy_bonus_modal_closed'),
    persistentBuyBonusDisabled: () => trackEvent('persistent_buy_bonus_disabled'),
}
